import React, { useState, useRef, useEffect } from 'react';
import './Chatbot.css';
import { GoogleGenAI } from "@google/genai";

const AIChatbotDemo = ({ isOpen, onClose, inline = false, headerColor }) => {
  const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
  const ai = apiKey ? new GoogleGenAI({ apiKey }) : null;

  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hello! 👋 I'm an AI chatbot demonstrating advanced capabilities. I can showcase:\n\n✨ Instant responses - I reply immediately to your questions\n🗺️ Website navigation - I can guide users through websites\n❓ FAQ handling - I answer common questions efficiently\n📝 Lead generation - I can collect and qualify customer information\n🌍 Multi-language support - I communicate in multiple languages\n\nAsk me about any of these features or how they work!",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const scrollToBottom = () => {
    // Scroll only the messages container, not the page
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [isOpen]);

  const sendMessage = async (e) => {
    if (!inputMessage.trim() || isLoading) return;

    // Prevent page scroll when sending message
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const userMessage = {
      id: messages.length + 1,
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);

    try {
      if (!ai) {
        throw new Error('API key not configured');
      }

      // System instruction for AI Chatbot demo
      const systemPrompt = `You are a professional AI chatbot assistant demonstrating advanced capabilities. Your role is to showcase and answer questions about these specific features:

1. INSTANT RESPONSES: You provide immediate, real-time answers to user questions without any delay
2. WEBSITE NAVIGATION: You can guide users through websites, help them find information, and direct them to relevant pages
3. FAQ HANDLING: You efficiently answer frequently asked questions, reducing the need for human support
4. LEAD GENERATION: You can collect customer information (name, email, phone, interests) and qualify leads automatically
5. MULTI-LANGUAGE SUPPORT: You can communicate in multiple languages based on user preference

PERSONALITY:
- Professional, friendly, and helpful
- Concise but informative (2-3 sentences max)
- Use natural, conversational language
- Be proactive in demonstrating capabilities

YOUR ROLE:
- Answer questions ABOUT these chatbot features and capabilities
- Explain how each feature works and its benefits
- Demonstrate the features through your responses
- Show examples of what the chatbot can do
- When asked about capabilities, explain them clearly

IMPORTANT GUIDELINES:
- Focus on explaining and demonstrating the chatbot's features (instant responses, website navigation, FAQ handling, lead generation, multi-language support)
- If someone asks "what can you do?", explain your 5 key features
- If someone asks about a specific feature, provide a detailed explanation with examples
- Demonstrate instant responses by replying quickly
- If user asks in a different language, respond in that language to show multi-language support
- When appropriate, offer to collect their information to demonstrate lead generation
- Keep responses conversational and engaging
- Use emojis sparingly and naturally
- DO NOT talk about website navigation as in directing to website sections - instead explain that you CAN help with website navigation as a feature

CONVERSATION HISTORY:
${messages.map(msg => `${msg.sender === 'user' ? 'User' : 'AI Chatbot'}: ${msg.text}`).join('\n')}

User: ${inputMessage}

AI Chatbot:`;

      const response = await ai.models.generateContent({
        model: "gemini-2.5-flash",
        contents: systemPrompt
      });

      let botResponse;
      if (response?.response?.text) {
        botResponse = response.response.text;
      } else if (response?.text) {
        botResponse = response.text;
      } else {
        botResponse = "I'm here to help! Could you rephrase your question?";
      }

      const botMessage = {
        id: messages.length + 2,
        text: botResponse.trim(),
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      let errorText = "I'm having a little trouble right now. Could you try asking again?";
      
      if (error.message === 'API key not configured') {
        errorText = "Hello! I'm your AI chatbot demo. To see me in action, please configure the API key. For now, I can still demonstrate my capabilities!";
      }
      
      const errorMessage = {
        id: messages.length + 2,
        text: errorText,
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      sendMessage(e);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="chatbot-side-panel">
      <div className="chatbot-container">
        <div 
          className="chatbot-header"
          style={inline && headerColor ? { 
            background: `linear-gradient(135deg, ${headerColor} 0%, ${headerColor}dd 100%)` 
          } : undefined}
        >
          <div className="chatbot-title">
            <i className="fas fa-comments"></i>
            <span>AI Chatbot</span>
          </div>
          {!inline && (
            <button className="close-button" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>

        <div className="chatbot-messages" ref={messagesContainerRef}>
          {messages.map((message) => (
            <div
              key={message.id}
              className={`message ${message.sender === 'user' ? 'user-message' : 'bot-message'}`}
            >
              <div className="message-content">
                {message.text}
              </div>
              <div className="message-timestamp">
                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="message bot-message">
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="chatbot-input">
          <textarea
            ref={inputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message here..."
            disabled={isLoading}
            rows="1"
          />
          <button
            onClick={(e) => sendMessage(e)}
            disabled={!inputMessage.trim() || isLoading}
            className="send-button"
            type="button"
          >
            <i className="fas fa-paper-plane"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIChatbotDemo;

